import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { useAuth } from '../hooks/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { SystemStatus } from '../components/SystemStatus'
import { getErrorMessage } from '../utils/errorHandler'

export const LoginPage: React.FC = () => {
  const [showAdminLogin, setShowAdminLogin] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [systemReady, setSystemReady] = useState(false)
  const [systemError, setSystemError] = useState<string | null>(null)
  const [adminCredentials, setAdminCredentials] = useState({ username: 'admin', password: 'admin123' })
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const { login: apiLogin, isLoggingIn, loginError } = useAuth()
  
  const handleLineLogin = async () => {
    setIsLoading(true)
    try {
      // Get real LINE OAuth URL from backend
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api'
      const response = await fetch(`${API_BASE_URL}/auth/line/authorize`)
      const data = await response.json()
      
      if (data.success && data.data.auth_url) {
        // Redirect to LINE OAuth
        window.location.href = data.data.auth_url
      } else {
        throw new Error('Failed to get LINE OAuth URL')
      }
    } catch (error) {
      console.error('Failed to initiate LINE login:', error)
      setIsLoading(false)
    }
  }
  
  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      await apiLogin(adminCredentials)
      // Navigate to admin dashboard on successful login
      navigate('/admin/dashboard')
    } catch (error) {
      console.error('Admin login failed:', error)
      // Error is handled by the useAuth hook
    }
  }

  // Demo login for testing footer navigation
  const handleDemoLogin = () => {
    console.log('[Auth Debug] Creating demo login')
    
    // Create a simple demo token that won't expire for testing
    const demoUser = {
      id: 1,
      type: 'parent' as const,
      display_name: 'Demo Parent',
      line_user_id: 'demo123'
    }
    
    // Create a mock JWT-like token for demo purposes
    const demoToken = `demo.${btoa(JSON.stringify({
      sub: '1',
      name: 'Demo Parent',
      type: 'parent',
      exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours from now
    }))}.signature`
    
    login(demoUser, demoToken)
    navigate('/dashboard')
  }
  
  // Show system status check first
  if (!systemReady) {
    return (
      <SystemStatus
        onReady={() => setSystemReady(true)}
        onError={(error) => setSystemError(error)}
      />
    )
  }

  // Show system error if health check failed - with bypass option
  if (systemError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-6xl mb-4">🚨</div>
            <h1 className="text-xl font-bold text-red-800 mb-2">ระบบไม่พร้อมใช้งาน</h1>
            <p className="text-red-600 text-sm mb-4">{systemError}</p>
            <p className="text-gray-600 text-xs mb-4">กรุณาติดต่อผู้ดูแลระบบ หรือลองใหม่ภายหลัง</p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 w-full"
              >
                รีเฟรชหน้าเว็บ
              </button>
              <button
                onClick={() => {
                  setSystemError(null)
                  setSystemReady(true)
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 w-full"
              >
                ข้ามการตรวจสอบระบบ (เสี่ยง)
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md w-full">
        {/* Main Login Card */}
        <div className="card-thai text-center">
          {/* Logo and Title */}
          <div className="mb-8">
            <div className="text-6xl mb-4">📚</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-thai-header">
              ระบบกวดวิชา ป.3
            </h1>
            <p className="text-gray-600 font-thai">
              ยินดีต้อนรับผู้ปกครองทุกท่าน
            </p>
          </div>
          
          {/* Features */}
          <div className="mb-8 text-left space-y-3">
            <p className="text-gray-700 font-thai font-medium mb-3">ระบบนี้ช่วยให้คุณ:</p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>ชำระค่าเรียนง่าย</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>ติดตามบทเรียน</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>รับประกาศสำคัญ</span>
              </div>
            </div>
          </div>
          
          {/* LINE Login Button */}
          <button
            onClick={handleLineLogin}
            disabled={isLoggingIn}
            className="btn-primary w-full mb-4 flex items-center justify-center gap-2"
          >
            {isLoggingIn ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                <span className="text-2xl">🟢</span>
                <span>เข้าสู่ระบบด้วย LINE</span>
              </>
            )}
          </button>
          
          {/* Security Info */}
          <div className="text-xs text-gray-500 space-y-1 mb-4">
            <div className="flex items-center justify-center gap-2">
              <span>🔒</span>
              <span>ข้อมูลปลอดภัย</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span>🇹🇭</span>
              <span>ภาษาไทยเท่านั้น</span>
            </div>
          </div>
          
          {/* Demo Login Button - Development Only */}
          {!import.meta.env.PROD && (
            <button
              onClick={handleDemoLogin}
              className="btn-secondary w-full mb-4 text-sm"
            >
              🧪 เข้าสู่ระบบทดสอบ (Demo)
            </button>
          )}
          
          {/* Admin Login Toggle */}
          <button
            onClick={() => setShowAdminLogin(!showAdminLogin)}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showAdminLogin ? 'ซ่อนการเข้าสู่ระบบผู้ดูแล' : 'เข้าสู่ระบบผู้ดูแล'}
          </button>
        </div>
        
        {/* Admin Login Form */}
        {showAdminLogin && (
          <div className="card-thai mt-4 animate-fade-in">
            <h2 className="text-lg font-semibold mb-4 font-thai-header">เข้าสู่ระบบผู้ดูแล</h2>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{getErrorMessage(loginError)}</p>
                </div>
              )}
              
              <div>
                <label className="label-thai required">ชื่อผู้ใช้</label>
                <input
                  type="text"
                  className="input-thai"
                  value={adminCredentials.username}
                  onChange={(e) => setAdminCredentials(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="admin"
                  required
                />
              </div>
              
              <div>
                <label className="label-thai required">รหัสผ่าน</label>
                <input
                  type="password"
                  className="input-thai"
                  value={adminCredentials.password}
                  onChange={(e) => setAdminCredentials(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="รหัสผ่าน"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoggingIn}
                className="btn-secondary w-full"
              >
                {isLoggingIn ? <LoadingSpinner size="small" /> : 'เข้าสู่ระบบ'}
              </button>
            </form>
          </div>
        )}
        
        {/* Contact Info */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            เบอร์ติดต่อ: 081-XXX-XXXX
          </p>
        </div>
      </div>
    </div>
  )
}